<template>
    <FormSection
        :full-height="false"
        title="Employee Scheduler"
        :application-id="visitDetails.Application"
        :show-product-pill="true"
        :show-background="false"
    >
        <template #content>
            <section class="p-5 d-flex flex-column text-center text-primary" v-if="isLoading">
                <i class="fa fa-spinner fa-spin font-size-5xl me-2 animated slow flash infinite"></i>
                <span class="mt-1">
                    <b>Loading...</b>
                </span>
            </section>
            <section id="Schedule_Visit_HH" class="task-details" v-else>
                <div class="container row m-0 p-lg-1 ps-md-2">
                    <div class="form-group col-12 mt-2">
                        <div>
                            <input type="radio" v-model="isNva" :value="false" /> Visit Task
                            <input type="radio" v-model="isNva" :value="true" /> Non Visit Task
                        </div>
                        <strong for="task-name" class="me-2">Employee:</strong>
                        <span class="bold"> {{ visitDetails.FirstName }} {{ visitDetails.LastName }}</span>
                        <p class="mt-2" v-if="!isNva">
                            <strong class="me-2">Agency:</strong>
                            <span>{{ visitDetails.AgencyName }}</span>
                        </p>
                    </div>
                    <div class="form-group col-12 col-md-8 col-lg-3 mt-lg-2" v-if="!isNva">
                        <label for="employee"> Visit Type: </label><span class="text-danger">*</span>
                        <TypeaheadInput
                            :getData="availableTasksFilter"
                            :value="selectedTask"
                            v-on:valueChanged="changeTaskList"
                            placeholder="Type to Select Visit Type"
                            :show-all-by-default="true"
                            :open-on-focus="true"
                            :must-select-value="true"
                            v-on:blurredInput="v$.selectedTask?.$touch"
                            :class="`${!v$.selectedTask?.$error ? '' : 'field-invalid'}`"
                        />
                        <span v-if="v$.selectedTask?.$error" class="field-invalid">Visit Type is required.</span>
                    </div>
                    <div class="form-group col-12 col-md-8 col-lg-3 mt-md-2" v-if="!isNva">
                        <label for="assigned-to-list"> Patient: <span class="text-danger">*</span></label>
                        <TypeaheadInput
                            placeholder="Type to Select Patient"
                            :getData="getPatient"
                            :value="patientId"
                            v-on:valueChanged="patientIdChanged"
                            :showAllByDefault="showAllByDefault"
                            v-on:blurredInput="v$.newVisitDetails.patientId.$touch"
                            :class="`${!v$.newVisitDetails.patientId.$error ? '' : 'field-invalid'}`"
                        />
                        <span v-if="v$.newVisitDetails.patientId.$error" class="field-invalid"
                            >Patient is required.</span
                        >
                    </div>
                    <div class="form-group col-12" v-if="!isNva">
                        <hr class="ms-0" />
                        <label>Visit Orders</label>
                        <div class="w-50" v-if="visitOrders.length > 0">
                            <div class="table-responsive">
                                <table class="table border border-gray">
                                    <tbody>
                                        <tr class="bg-table-header">
                                            <th>Discipline</th>
                                            <th>Frequency</th>
                                            <th>Effective Date</th>
                                        </tr>
                                        <tr v-for="(order, index) of visitOrders" :key="index">
                                            <td>{{ order.discipline }}</td>
                                            <td class="white-space-pre">{{ order.frequency }}</td>
                                            <td class="white-space-pre">{{ order.effectiveDate }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <p v-else>There are no current orders for visits.</p>
                    </div>
                    <div class="form-group col-12" v-if="!isNva">
                        <hr class="ms-0" />
                        <p v-if="!currentEpisodeDetails">Select patient to see the current episode</p>
                        <div v-else>
                            <p>
                                To schedule visits for the selected user, click on the desired dates in the calendar
                                below:
                            </p>
                            <div class="form-group col-12 ps-md-2">
                                <MultiDatePickerHH
                                    v-if="currentEpisodeDetails"
                                    :result="visitDates"
                                    :current-episode-details="currentEpisodeDetails"
                                    v-on:updateResult="updateResult"
                                    :visitDates="visitDatesList"
                                >
                                    <input class="form-control" />
                                </MultiDatePickerHH>
                                <div v-if="v$.visitDates?.$error" class="invalid-feedback d-block">
                                    Visit Dates is Required.
                                </div>
                            </div>
                            <div v-if="currentEpisodeDetails" class="d-flex col-12 justify-content-center">
                                <div class="form-group col-md-6">
                                    <div class="d-flex flex-wrap flex-md-nowrap">
                                        <div class="me-2 me-md-3">
                                            <div class="calendar-legend scheduled"></div>
                                            Scheduled
                                        </div>
                                        <div class="me-2 me-md-3">
                                            <div class="calendar-legend completed"></div>
                                            Completed
                                        </div>
                                        <div class="me-2 me-md-3">
                                            <div class="calendar-legend missed"></div>
                                            Missed
                                        </div>
                                        <div class="me-2 me-md-3">
                                            <div class="calendar-legend multiple"></div>
                                            Multiple
                                        </div>
                                        <div class="me-2 me-md-3">
                                            <div class="calendar-legend disabled"></div>
                                            Disabled
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="form-group col-12 col-md-6 mt-2" v-if="isNva">
                        <label for="date-to"> NVA Date </label><span class="text-danger">*</span>
                        <DatePicker :getDate="onDateChange">
                            <div class="input-group date datepickerInput">
                                <input
                                    type="text"
                                    name="new-task-single-date"
                                    class="form-control"
                                    placeholder="MM/DD/YYYY"
                                    :value="formatDateInput(newTaskSingleDate)"
                                    :on-change="(e: any) => onDateChange(e.target.value, e.target.name)"
                                    autocomplete="off"
                                    @blur="v$.newTaskSingleDate?.$touch"
                                    :class="`${!v$.newTaskSingleDate?.$error ? '' : 'field-invalid mt-0'}`"
                                />
                                <div class="input-group-addon input-group-append">
                                    <div class="input-group-text">
                                        <i class="fa fa-calendar"></i>
                                    </div>
                                </div>
                            </div>
                            <span v-if="v$.newTaskSingleDate?.$error" class="field-invalid">Date is required.</span>
                        </DatePicker>
                        <span v-if="v$.newTaskFlexDates?.$error" class="field-invalid">Date is required.</span>
                    </div>
                    <div class="form-group col-12 col-md-6 mt-2" v-if="isNva">
                        <label for="task"> Non-Visit Activity </label><span class="text-danger">*</span>
                        <TypeaheadInput
                            :getData="nvaAvailableTasksFilter"
                            :value="nvaActivity"
                            v-on:valueChanged="nvaActivityChanged"
                            :show-all-by-default="true"
                            :open-on-focus="true"
                            :must-select-value="true"
                            placeholder="Type to Filter Task"
                            v-on:blurredInput="v$.nvaActivity.$touch"
                            :class="`${!v$.nvaActivity?.$error ? '' : 'field-invalid'}`"
                        />
                        <span v-if="v$.nvaActivity?.$error" class="field-invalid">Activity is required.</span>
                    </div>
                    <div class="form-group col-12 col-md-6 mt-2" v-if="isNva">
                        <label for="branch">
                            Branch
                            <span class="text-danger ms-1">*</span>
                        </label>
                        <select class="form-select" v-model="selectedBranch" @blur="v$.selectedBranch.id.$touch">
                            <option value="null">Select Branch</option>
                            <option v-for="branch of selectedBranches" :value="branch" :key="branch.name">
                                {{ branch.name }}
                            </option>
                        </select>
                        <span v-if="v$.selectedBranch?.id?.$error" class="field-invalid">Branch is required.</span>
                    </div>
                    <div class="form-group col-12 col-md-6 mt-2" v-if="isNva">
                        <label for="travel-start-time"> Travel Start Time: </label>
                        <div>
                            <div class="input-group flex-nowrap">
                                <TimePicker id="travel-start-time" @update-props="changeTravelStartTime">
                                    <input
                                        placeholder="hh:mm AM"
                                        :class="`form-control`"
                                        type="text"
                                        autocomplete="off"
                                        maxlength="8"
                                        :value="visitDetails.StartTime"
                                    />
                                </TimePicker>
                            </div>
                        </div>
                    </div>
                    <div class="form-group col-12 col-md-6 mt-2" v-if="isNva">
                        <label for="travel-end-time"> Travel End Time: </label>
                        <div>
                            <div class="input-group flex-nowrap">
                                <TimePicker id="travel-end-time" @update-props="changeTravelEndTime">
                                    <input
                                        placeholder="hh:mm AM"
                                        :class="`form-control`"
                                        type="text"
                                        autocomplete="off"
                                        maxlength="8"
                                        :value="visitDetails.EndTime"
                                    />
                                </TimePicker>
                            </div>
                        </div>
                    </div>
                    <div class="form-group col-12 col-md-6 mt-2" v-if="isNva">
                        <label for="visit-start-time"> NVA Start Time: <span class="text-danger">*</span></label>
                        <TimePicker @update-props="changeVisitTimeIn">
                            <input
                                id="nva-start-time"
                                type="text"
                                class="form-control"
                                autocomplete="off"
                                :value="visitDetails.TimeIn"
                                placeholder="hh:mm AM"
                            />
                        </TimePicker>
                    </div>
                    <div class="form-group col-12 col-md-6 mt-2" v-if="isNva">
                        <label for="visit-end-time"> NVA End Time: <span class="text-danger">*</span></label>
                        <TimePicker @update-props="changeVisitTimeOut">
                            <input
                                placeholder="hh:mm AM"
                                id="nva-end-time"
                                type="text"
                                class="form-control"
                                autocomplete="off"
                                :value="visitDetails.TimeOut"
                            />
                        </TimePicker>
                    </div>

                    <div class="form-group col-12 col-md-6 mt-2" v-if="isNva">
                        <label for="destination"> Destination </label>
                        <select id="destination" class="form-select" v-model="Destination">
                            <option v-for="(Destination, index) in DestinationList" :value="index" :key="Destination">
                                {{ Destination }}
                            </option>
                        </select>
                    </div>

                    <div class="form-group col-12 col-md-6 mt-2" v-if="isNva">
                        <label for="associated-mileage"> Associated Mileage: </label>
                        <input
                            id="associated-mileage"
                            type="text"
                            class="form-control"
                            placeholder="Enter Amount"
                            v-model="associatedMileage"
                            number-only
                            maxlength="7"
                        />
                    </div>

                    <section class="d-flex flex-column col-12 mt-2" v-if="isNva">
                        <div class="form-group">
                            <label for="comments"> Comments </label>
                            <textarea
                                :class="`form-control commentInputClass`"
                                rows="4"
                                name="referral-comments"
                                placeholder="Enter Comments"
                                maxlength="200"
                                v-model="comments"
                            ></textarea>
                        </div>
                    </section>
                </div>
            </section>
            <div class="d-flex flex-wrap-reverse flex-md-nowrap justify-content-center my-2" v-if="!isLoading">
                <span v-if="isSaving">
                    <button class="btn btn-primary">
                        <i class="fa fa-spinner fa-spin animated slow flash infinite"></i>
                    </button>
                </span>
                <span v-else>
                    <button class="btn btn-primary" @click="scheduleVisit(SaveEnum.SaveAndExit)">Save and Exit</button>
                    <SaveNotifyUser
                        :agency-id="visitDetails.AgencyId"
                        button-name="Save and Notify Employee"
                        v-on:handleSaveVisit="scheduleVisit(SaveEnum.SaveAndNotifyEmployee)"
                    />
                    <button class="ms-2 btn btn-primary" @click="scheduleVisit(SaveEnum.SaveAndAddAnother)">
                        Save and Add Another
                    </button>
                    <button type="button" class="ms-2 btn btn-md btn-outline-secondary" @click="cancel()">
                        Cancel
                    </button>
                </span>
            </div>
        </template>
    </FormSection>
</template>

<script lang="ts" src="./ScheduleVisitHomeHealth.ts"></script>
<style lang="scss" src="./ScheduleVisitHomeHealth.scss"></style>
